import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Terms.css';

const Terms = () => {
  return (
    <div className="terms-page">
      <Helmet>
        <title>Terms and Conditions | Media Mars Limited</title>
        <meta name="description" content="Terms and Conditions for the Media Mars Limited website and services. Governing law: United Kingdom." />
        <link rel="canonical" href="https://www.media-mars.com/terms" />
      </Helmet>

      <section className="terms-hero">
        <div className="terms-hero__inner">
          <h1>Terms and Conditions</h1>
          <p className="terms-updated">Last updated: 25 March 2026</p>
        </div>
      </section>

      <section className="terms-content">
        <div className="terms-content__inner">

          {/* 1 */}
          <div className="terms-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to the Media Mars Ltd website. By accessing, browsing, and using our website,
              you accept these terms and conditions in full. If you do not agree with any part of
              them, please stop using the website immediately.
            </p>
          </div>

          {/* 2 */}
          <div className="terms-section">
            <h2>2. Definitions</h2>
            <ul className="terms-definitions">
              <li><strong>"Website"</strong> means the Media Mars Ltd website and all its pages, content, and services.</li>
              <li><strong>"User", "You", "Your"</strong> means any person or organisation that accesses or uses the website.</li>
              <li><strong>"We", "Our", "Us"</strong> means Media Mars Ltd and its employees, representatives, and affiliates.</li>
              <li><strong>"Services"</strong> means any IT consulting, software, platform (including SpocoDoc), or related services we provide.</li>
            </ul>
          </div>

          {/* 3 */}
          <div className="terms-section">
            <h2>3. Use of the Website</h2>
            <p>
              You agree to use this website responsibly and in compliance with all applicable laws.
              Any misuse, including attempting to disrupt or compromise the website, may result in
              restricted access or legal action.
            </p>
          </div>

          {/* 4 */}
          <div className="terms-section">
            <h2>4. Intellectual Property</h2>
            <p>
              All content on this website - including designs, text, images, logos, illustrations,
              and software - is owned by Media Mars Ltd, its licensors, or content providers. This
              content is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p>
              You may not modify, reproduce, distribute, or publicly display any content without
              our express written permission. Media Mars Ltd respects the intellectual property
              rights of others and expects users to do the same.
            </p>
          </div>

          {/* 5 */}
          <div className="terms-section">
            <h2>5. Our Services</h2>
            <p>
              Media Mars Ltd provides IT strategy consulting, business automation, data analytics,
              system integration, IT coaching, hardware supply, custom software development, and
              our document management platform SpocoDoc.
            </p>
            <p>
              We reserve the right to modify, suspend, or discontinue any products, services,
              or features at any time without prior notice.
            </p>
          </div>

          {/* 6 */}
          <div className="terms-section">
            <h2>6. Limitation of Liability</h2>
            <div className="terms-highlight">
              <p>
                We work hard to keep our content accurate, but we cannot guarantee it is always
                complete or up to date. The website and its content are provided on an "as is"
                and "as available" basis.
              </p>
              <p>
                Media Mars Ltd disclaims all warranties, express or implied, including those
                relating to accuracy, non-infringement, and fitness for a particular purpose.
                We shall not be liable for any errors, omissions, or decisions made based on
                information provided on this website.
              </p>
            </div>
          </div>

          {/* 7 */}
          <div className="terms-section">
            <h2>7. User Accounts</h2>
            <p>
              Some of our services may require you to create an account. You are responsible
              for keeping your login details secure and for all activity under your account.
              Report any suspicious activity to us immediately.
            </p>
            <p>
              If your account is terminated (by you or by us), you may lose access to your
              data and content.
            </p>
          </div>

          {/* 8 */}
          <div className="terms-section">
            <h2>8. Data Protection and Privacy</h2>
            <p>
              We take your privacy seriously. All personal data is handled in accordance with our{' '}
              <Link to="/privacy">Privacy Policy</Link>, which explains what we collect, why,
              and your rights under UK data protection law.
            </p>
          </div>

          {/* 9 */}
          <div className="terms-section">
            <h2>9. Cookies</h2>
            <p>
              Our website uses a cookie consent banner. Analytics cookies (Google Analytics
              and Microsoft Clarity) are only loaded if you choose to accept them. You can
              find full details in our <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </div>

          {/* 10 */}
          <div className="terms-section">
            <h2>10. External Links</h2>
            <p>
              Our website may contain links to third-party websites for your convenience.
              We do not endorse, control, or accept responsibility for the content or
              practices of these external sites.
            </p>
          </div>

          {/* 11 */}
          <div className="terms-section">
            <h2>11. Prohibited Activities</h2>
            <p>You must not:</p>
            <ul>
              <li>Use automated tools (robots, scrapers, spiders) to access or copy website content</li>
              <li>Attempt to hack, probe, or breach the website's security measures</li>
              <li>Access data or systems you are not authorised to use</li>
              <li>Upload or share content that infringes others' intellectual property rights</li>
              <li>Engage in harassment, discrimination, or inappropriate conduct towards our employees, representatives, or other users</li>
            </ul>
            <p>
              Violations may result in account termination, restricted access, reporting to
              authorities, or legal action.
            </p>
          </div>

          {/* 12 */}
          <div className="terms-section">
            <h2>12. Respectful Conduct</h2>
            <p>
              We expect all users to interact with Media Mars Ltd employees, representatives,
              and other users in a respectful and professional manner. Any form of harassment,
              bullying, or discrimination will not be tolerated.
            </p>
          </div>

          {/* 13 */}
          <div className="terms-section">
            <h2>13. Reporting Misconduct</h2>
            <p>
              If you witness or experience misconduct - whether a security issue or inappropriate
              behaviour - please report it to us at{' '}
              <a href="mailto:info@media-mars.com">info@media-mars.com</a>. We are committed
              to addressing all reports promptly.
            </p>
          </div>

          {/* 14 */}
          <div className="terms-section">
            <h2>14. Using Our Services on Behalf of an Organisation</h2>
            <p>
              If you use our services on behalf of an organisation, that organisation accepts
              these terms and agrees to indemnify Media Mars Ltd and its affiliates, officers,
              agents, and employees from any claim arising from the use of our services or
              violation of these terms.
            </p>
          </div>

          {/* 15 */}
          <div className="terms-section">
            <h2>15. Indemnification</h2>
            <p>
              By using this website, you agree to indemnify and hold harmless Media Mars Ltd,
              its affiliates, officers, directors, and employees from any claims, damages,
              or expenses (including legal fees) arising from your breach of these terms
              or any harm you cause to a third party.
            </p>
          </div>

          {/* 16 */}
          <div className="terms-section">
            <h2>16. Service Communications</h2>
            <p>
              To provide our services, we may send you service announcements, administrative
              messages, and other relevant information. Continued use of our services means
              you agree to receive these communications.
            </p>
          </div>

          {/* 17 */}
          <div className="terms-section">
            <h2>17. Force Majeure</h2>
            <p>
              Media Mars Ltd shall not be liable for any delay or failure to perform resulting
              from causes outside our reasonable control, including but not limited to: acts of
              God, war, terrorism, riots, fire, floods, strikes, or shortages of materials,
              transportation, or energy.
            </p>
          </div>

          {/* 18 */}
          <div className="terms-section">
            <h2>18. Waiver and Severability</h2>
            <p>
              If we fail to enforce a particular provision, that does not waive our right to
              do so later. If any provision is found to be unenforceable, the remaining terms
              remain in full effect.
            </p>
          </div>

          {/* 19 */}
          <div className="terms-section">
            <h2>19. Governing Law and Disputes</h2>
            <div className="terms-highlight">
              <p>
                These terms are governed by the laws of the United Kingdom. Any disputes
                arising from these terms will be resolved in the competent courts of the UK.
              </p>
              <p>
                We encourage you to contact us first to resolve any issue amicably. If a
                resolution cannot be reached, disputes may be directed to binding arbitration
                in the UK.
              </p>
            </div>
          </div>

          {/* 20 */}
          <div className="terms-section">
            <h2>20. Changes to These Terms</h2>
            <p>
              We may update these terms from time to time - for example, to reflect changes
              in the law or our services. The "last updated" date at the top of this page
              will always reflect the most recent version. Changes become effective 14 days
              after posting, unless they address new service features or legal requirements,
              in which case they take effect immediately.
            </p>
          </div>

          {/* 21 */}
          <div className="terms-section">
            <h2>21. Entire Agreement</h2>
            <p>
              These terms, together with our <Link to="/privacy">Privacy Policy</Link> and any
              other documents referenced here, form the entire agreement between you and Media
              Mars Ltd. They supersede all prior agreements, representations, and understandings.
            </p>
          </div>

          {/* 22 */}
          <div className="terms-section">
            <h2>22. Contact Us</h2>
            <p>
              If you have questions, feedback, or concerns about these terms, please get in touch:
            </p>
            <ul className="terms-contact">
              <li><strong>Email:</strong> <a href="mailto:info@media-mars.com">info@media-mars.com</a></li>
              <li><strong>Address:</strong> Rombourne Business Centres, Kingston House, Lydiard Fields, Swindon, Wiltshire SN5 8UB, UK</li>
            </ul>
            <p>
              Any feedback or suggestions you provide may be used by Media Mars Ltd without
              restriction or obligation.
            </p>
          </div>

          <div className="terms-back">
            <Link to="/" className="terms-back-link">&larr; Back to Home</Link>
          </div>

        </div>
      </section>
    </div>
  );
};

export default Terms;
